export type SimulatorFocus = "balanced" | "cr" | "upt";

export const TARGET_PLAN_SAFETY_RATE = 0.02;

export type TargetGapSimulation = {
  suggestedCr: number;
  suggestedUpt: number;
  crContribution: number;
  uptContribution: number;
  additionalSales: number;
  targetResult: number;
};

export type TargetPlanOperationalGuidance = {
  crIncreasePoints: number;
  visitorsPerExtraReceipt: number | null;
  uptIncreasePerReceipt: number;
  receiptsPerExtraUnit: number | null;
};

export function calculateTargetPlanOperationalGuidance(
  currentCr: number,
  suggestedCr: number,
  currentUpt: number,
  suggestedUpt: number,
): TargetPlanOperationalGuidance {
  const crIncreasePoints = Number(Math.max(0, suggestedCr - currentCr).toFixed(10));
  const uptIncreasePerReceipt = Number(Math.max(0, suggestedUpt - currentUpt).toFixed(10));

  return {
    crIncreasePoints,
    visitorsPerExtraReceipt: crIncreasePoints > 0
      ? Math.max(1, Math.floor(100 / crIncreasePoints + 1e-9))
      : null,
    uptIncreasePerReceipt,
    receiptsPerExtraUnit: uptIncreasePerReceipt > 0
      ? Math.max(1, Math.floor(1 / uptIncreasePerReceipt + 1e-9))
      : null,
  };
}

export function calculateTargetGapSimulation(
  currentSales: number,
  gapToDate: number,
  currentCr: number,
  currentUpt: number,
  focus: SimulatorFocus,
): TargetGapSimulation {
  const baseline = Math.max(0, currentSales);
  const gap = Math.max(0, gapToDate);
  const targetToDate = baseline + gap;
  const plannedGap = gap * (1 + TARGET_PLAN_SAFETY_RATE);
  const plannedTarget = baseline + plannedGap;
  const requiredMultiplier = baseline > 0 ? Math.max(1, plannedTarget / baseline) : 1;

  let suggestedCr = currentCr;
  let suggestedUpt = currentUpt;
  if (baseline > 0 && currentCr > 0 && currentUpt > 0 && requiredMultiplier > 1) {
    if (focus === "balanced") {
      suggestedCr = Math.min(100, currentCr * Math.sqrt(requiredMultiplier));
      const crMultiplier = suggestedCr / currentCr;
      suggestedUpt = currentUpt * requiredMultiplier / crMultiplier;
    } else if (focus === "cr") {
      suggestedCr = Math.min(100, currentCr * requiredMultiplier);
    } else {
      suggestedUpt = currentUpt * requiredMultiplier;
    }
  }

  const crMultiplier = currentCr > 0 ? suggestedCr / currentCr : 1;
  const uptMultiplier = currentUpt > 0 ? suggestedUpt / currentUpt : 1;
  const simulatedSales = baseline * crMultiplier * uptMultiplier;
  const interactionEffect = baseline * (crMultiplier - 1) * (uptMultiplier - 1);
  const crContribution = baseline * (crMultiplier - 1) + interactionEffect / 2;
  const uptContribution = baseline * (uptMultiplier - 1) + interactionEffect / 2;
  const additionalSales = Math.max(0, crContribution + uptContribution);
  const rawTargetResult = simulatedSales - targetToDate;

  return {
    suggestedCr,
    suggestedUpt,
    crContribution,
    uptContribution,
    additionalSales,
    targetResult: Math.abs(rawTargetResult) < 0.5 ? 0 : rawTargetResult,
  };
}
